/*
price-errors.js
Classifies price provider failures. "print.com cannot price this configuration"
is an expected, per-row outcome; auth / rate limit / outage / network failures
are systemic and must reach the caller as a loud failure instead of being
degraded into a cacheable empty price.
*/

export const PRICE_ERROR_KIND = {
  UNPRICEABLE: "unpriceable",
  AUTH: "auth",
  RATE_LIMIT: "rate_limit",
  UPSTREAM: "upstream",
  NETWORK: "network",
  TIMEOUT: "timeout",
};

const UNPRICEABLE_STATUSES = new Set([400, 404, 409, 422]);

function stringifyBody(body) {
  try {
    return JSON.stringify(body);
  } catch (error) {
    return String(body);
  }
}

export class PriceProviderError extends Error {
  constructor({ kind, status = null, body = null, message, cause } = {}) {
    super(message || stringifyBody(body) || kind);
    this.name = "PriceProviderError";
    this.kind = kind;
    this.status = status;
    this.body = body;
    if (cause) this.cause = cause;
  }

  // The ID-based price route reads this alias to return unpriceable errors as 422.
  get providerRejection() {
    return this.kind === PRICE_ERROR_KIND.UNPRICEABLE;
  }
}

const KNOWN_KINDS = new Set(Object.values(PRICE_ERROR_KIND));

/**
 * True only for a recognised, non-unpriceable price failure. An error this
 * module did not produce answers false because its kind is unknown, not because
 * it is safe.
 *
 * Do not read `!isSystemicPriceError(err)` as "degrade this row" - that would
 * swallow a Supabase failure or a TypeError. Use `isUnpriceable(err)` for the
 * degrade decision, and let everything else propagate.
 */
export function isSystemicPriceError(error) {
  if (!isPriceProviderError(error)) return false;
  return error.kind !== PRICE_ERROR_KIND.UNPRICEABLE;
}

export function isUnpriceable(error) {
  return (
    isPriceProviderError(error) && error.kind === PRICE_ERROR_KIND.UNPRICEABLE
  );
}

// Only inspect provider error fields, never configuration values or arbitrary
// body text. A mention of "copies" alone does not establish a quantity error.
function isQuantityValidationBody(body) {
  if (!body || typeof body !== "object") return false;
  if (body.code === "QUANTITY_UNAVAILABLE") return true;
  const error = typeof body.error === "object" ? body.error : null;
  if (error?.code === "QUANTITY_UNAVAILABLE") return true;
  return [
    body.errorMessage,
    body.message,
    body.error,
    error?.errorMessage,
    error?.message,
  ].some(
    (message) =>
      typeof message === "string" &&
      /(?:failed to validate copies range set|copies does not match any range set)/i.test(
        message
      )
  );
}

export function isQuantityUnavailable(error) {
  return isUnpriceable(error) && isQuantityValidationBody(error.body);
}

function isPriceProviderError(error) {
  if (error instanceof PriceProviderError) return true;
  return Boolean(error) && KNOWN_KINDS.has(error.kind);
}

/**
 * The proxy maps print.com's option-validation failures onto a 500 (see
 * handleApiError in app/api/print/proxy/route.js), so the status alone cannot
 * separate "bad configuration" from "print.com is down".
 */
function isValidationBody(body) {
  if (!body || typeof body !== "object") return false;
  if (body.error === "Validation Error" || isQuantityValidationBody(body))
    return true;

  const message = typeof body.message === "string" ? body.message : "";
  return (
    message.includes("Failed to validate options") ||
    message.includes("Configuration not valid")
  );
}

export function classifyPriceFailure({ status, body }) {
  if (status === 401 || status === 403) return PRICE_ERROR_KIND.AUTH;
  if (status === 429) return PRICE_ERROR_KIND.RATE_LIMIT;
  if (UNPRICEABLE_STATUSES.has(status)) return PRICE_ERROR_KIND.UNPRICEABLE;
  if (status === 500 && isValidationBody(body)) {
    return PRICE_ERROR_KIND.UNPRICEABLE;
  }
  return PRICE_ERROR_KIND.UPSTREAM;
}

export function classifyThrownError(error) {
  const name = error?.name || "";
  const message = typeof error?.message === "string" ? error.message : "";

  if (
    name === "AbortError" ||
    name === "TimeoutError" ||
    message.toLowerCase().includes("timeout")
  ) {
    return PRICE_ERROR_KIND.TIMEOUT;
  }

  if (
    error instanceof TypeError ||
    name === "TypeError" ||
    name === "FetchError" ||
    message.toLowerCase().includes("fetch failed") ||
    message.toLowerCase().includes("network")
  ) {
    return PRICE_ERROR_KIND.NETWORK;
  }

  return PRICE_ERROR_KIND.UPSTREAM;
}
